import type { FallbackRender } from '@sentry/react'
import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useRouteError } from 'react-router-dom'

import { Alert } from '@hrworks/sui-core/Alert'
import { Button } from '@hrworks/sui-core/Button'

import { S } from './ErrorFallback.styles'
import type { ErrorFallbackVariantProps } from './ErrorFallback.types'

/**
 * Shared fallback UI for Sentry.ErrorBoundary. `variant='root'` fills the viewport and
 * offers a full-page reload; `variant='route'` sits inside the app chrome and offers
 * a scoped reset so the shell (nav, header) stays mounted.
 */
export const ErrorFallback = ({ error, resetError, variant }: ErrorFallbackVariantProps) => {
  const intl = useIntl()
  const isDev = import.meta.env.MODE !== 'production'
  const fullscreen = variant === 'root'

  const handlePrimaryAction = () => {
    if (fullscreen) {
      window.location.reload()
    } else {
      resetError()
    }
  }

  // This component is the last-resort UI Sentry.ErrorBoundary renders when
  // something else has already gone wrong — the one place a `defaultMessage`
  // is worth keeping despite the general i18n-check policy of not masking
  // missing keys with a hardcoded fallback. The check still requires these keys
  // to exist in both locale files.
  const primaryLabel = fullscreen
    ? intl.formatMessage({ id: 'errors.reload', defaultMessage: 'Reload page' })
    : intl.formatMessage({ id: 'errors.retry', defaultMessage: 'Try again' })

  return (
    <S.Container $fullscreen={fullscreen} role="alert" aria-live="assertive">
      <S.Title>
        {intl.formatMessage({ id: 'errors.title', defaultMessage: 'Something went wrong' })}
      </S.Title>
      <S.Message>
        {intl.formatMessage({
          id: 'errors.message',
          defaultMessage: "We're sorry for the inconvenience. The issue has been reported.",
        })}
      </S.Message>

      <Alert color="danger">{error instanceof Error ? error.message : String(error)}</Alert>

      {isDev && error instanceof Error && error.stack && (
        <S.Details>
          <summary>
            {intl.formatMessage({ id: 'errors.details', defaultMessage: 'Error details' })}
          </summary>
          <S.Pre>{error.stack}</S.Pre>
        </S.Details>
      )}

      <S.Actions>
        <Button color="primary" onClick={handlePrimaryAction}>
          {primaryLabel}
        </Button>
      </S.Actions>
    </S.Container>
  )
}

export const RootErrorFallback: FallbackRender = (props) => (
  <ErrorFallback {...props} variant="root" />
)

export const RouteErrorFallback: FallbackRender = (props) => (
  <ErrorFallback {...props} variant="route" />
)

/**
 * `errorElement` adapter for React Router 7 data routers.
 *
 * Routes in `createBrowserRouter` catch thrown errors (from loaders, actions,
 * components) and expose them via `useRouteError()`. This wrapper reads from
 * the router context, reports to Sentry, and renders the shared fallback so
 * the chrome (nav, header) stays mounted.
 */
export const RouteErrorBoundary = () => {
  const error = useRouteError()

  useEffect(() => {
    // React Router swallows the error from React's normal error tree, so it
    // never hits the outer Sentry.ErrorBoundary. Report it explicitly.
    Sentry.captureException(error)
  }, [error])

  const errorInstance = error instanceof Error ? error : new Error(String(error))

  // Reset = navigate away; for a chunk-load error, a soft reload of the route
  // via `location.reload()` is the most reliable recovery under jsdom + SPA.
  const resetError = () => window.location.reload()

  return (
    <ErrorFallback
      variant="route"
      error={errorInstance}
      componentStack=""
      eventId=""
      resetError={resetError}
    />
  )
}
